import { useEffect, useMemo, useState, type ChangeEvent } from "react";

import {
  Button,
  Select,
  type SelectEvent,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { useTranslation } from "react-i18next";

import styles from "../SchoolCandidates.module.scss";
import { Role, type SaveResult } from "../../../models";

type CountyAddressPickupControlsProps = {
  authCounties: CodeBook[];
  countyId: number | undefined;
  shouldValidateRequired: boolean;
  isAddressPickupInProgress: boolean;
  currentRole?: Role;
  onCountyChange: (event: ChangeEvent<SelectEvent>) => void;
  onStartAddressPickup: (countyId: number) => Promise<SaveResult<number>>;
};

export const CountyAddressPickupControls = ({
  authCounties,
  countyId,
  shouldValidateRequired,
  isAddressPickupInProgress,
  currentRole,
  onCountyChange,
  onStartAddressPickup,
}: CountyAddressPickupControlsProps) => {
  const { t } = useTranslation();

  const [isStartAddressPickupDisabled, setIsStartAddressPickupDisabled] =
    useState(false);
  const [validationError, setValidationError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    setValidationError("");

    if (isAddressPickupInProgress) {
      setStatusMessage(
        t("common.schoolCandidateModel.addressPickupInfoMessage")
      );

      return;
    }

    setStatusMessage("");
  }, [isAddressPickupInProgress]);

  useEffect(() => {
    setValidationError("");
    setStatusMessage("");
    setIsStartAddressPickupDisabled(false);
  }, [countyId]);

  const handleStartAddressPickupClick = async () => {
    if (!countyId) {
      return;
    }

    setIsStartAddressPickupDisabled(true);
    setValidationError("");
    setStatusMessage("");

    const result = await onStartAddressPickup(countyId);

    if (!result.isValid && result.validationFailures?.length) {
      setValidationError(
        result.validationFailures.map((failure) => failure.message).join(" ")
      );
      setIsStartAddressPickupDisabled(false);

      return;
    }

    setStatusMessage(t("common.schoolCandidateModel.addressPickupInfoMessage"));
  };

  const shouldShowStatusMessage = useMemo(
    () => !validationError && (isAddressPickupInProgress || statusMessage),
    [isAddressPickupInProgress, statusMessage, validationError]
  );

  return (
    <>
      <Select
        label={t("common.schoolCandidateModel.uoguCounty")}
        name="countyId"
        required
        infoText={t("common.schoolCandidateModel.uoguCountyIdInfoText")}
        value={countyId}
        onChange={onCountyChange}
        selectData={codeBookToSelectData(authCounties)}
        errorText={
          shouldValidateRequired && !countyId
            ? t("validations.required")
            : undefined
        }
        emptyOption={
          currentRole && [Role.CAS, Role.Helpdesk].includes(currentRole)
            ? { value: undefined, text: "" }
            : undefined
        }
        disabled={authCounties.length === 1}
      />
      <div className={styles.addressPickupButton}>
        <Button
          onClick={handleStartAddressPickupClick}
          size="sm"
          disabled={isStartAddressPickupDisabled}
        >
          Pokreni dohvat novih adresa
        </Button>
      </div>
      {validationError && (
        <div className="mt-2 text-danger" style={{ fontSize: "0.8rem" }}>
          {validationError}
        </div>
      )}
      {shouldShowStatusMessage && (
        <div className="mt-2 text-info" style={{ fontSize: "0.8rem" }}>
          {statusMessage}
        </div>
      )}
    </>
  );
};
